// external
import React, { FC, useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Paper,
  Box,
  IconButton,
  Fade,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Button,
  InputAdornment,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { format, parseISO } from 'date-fns';

// internal
import './coe-weekly-plan-builder.component.css';
import { Team } from '../../../models/training.models';
import COETrainingTemplateCard, {
  TrainingTemplate,
} from '../../coe-training-template-card/coe-training-template-card.component';
import useService from '../../../hooks/service.hook';
import TrainingLibraryService from '../../../services/training-library.service';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import { palette } from '../../../theme';
import { FieldErrors, useFieldErrors } from '../../form-validation';

interface COEWeeklyPlanBuilderProps {
  open: boolean;
  onClose: () => void;
  team: Team;
  planId?: string | null;
  type: 'create' | 'edit' | null;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
const DAY_ABBR = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

const formatSessionTime = (time: string): string => {
  if (!time) return '';
  try {
    if (time.includes('T') || time.includes('-')) {
      return format(parseISO(time), 'h:mm a');
    }
    return format(new Date(`2000-01-01T${time}`), 'h:mm a');
  } catch {
    return time;
  }
};

const COEWeeklyPlanBuilderComponent: FC<COEWeeklyPlanBuilderProps> = (props) => {
  const trainingLibraryService = useService(TrainingLibraryService);
  const { showSnackbar } = useSnackbarHelper();

  type PlanField = 'title' | 'description';

  const [loading, setLoading] = useState<boolean>(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const { fieldErrors, setFieldErrors, clearFieldError, resetFieldErrors } = useFieldErrors<PlanField>();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [showDayWarning, setShowDayWarning] = useState(false);
  const [trainingTemplates, setTrainingTemplates] = useState<TrainingTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedSportType, setSelectedSportType] = useState<string>('');
  const [selectedFocus, setSelectedFocus] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [dayAssignments, setDayAssignments] = useState<Record<string, TrainingTemplate[]>>({});
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [plan, setPlan] = useState<any | null>(null);

  useEffect(() => {
    if (!props.open) return;

    getTrainingTemplates();
    setSearchQuery('');
    setSelectedSport('');
    setSelectedSportType('');
    setSelectedFocus('');
    setSelectedDay(null);
    setShowDayWarning(false);
    setTemplateError(null);
    resetFieldErrors();

    if (props.planId) {
      getWeeklyPlan();
    } else {
      setPlan(null);
      setTitle('');
      setDescription('');
      setDayAssignments({});
    }
  }, [props.open, props.planId]);

  useEffect(() => {
    if (props.type === 'edit' && plan && trainingTemplates.length > 0) {
      setTitle(plan.title);
      setDescription(plan.description);

      const planData = plan.plan;
      const mappedDayAssignments = DAYS_OF_WEEK.reduce((acc, day) => {
        const lowercaseDay = day.toLowerCase();
        const templateIds = planData[lowercaseDay] || [];
        const templates = templateIds
          .map((templateId: string) => trainingTemplates.find((t) => t.id === templateId))
          .filter((template): template is TrainingTemplate => template !== undefined);
        acc[day] = templates;
        return acc;
      }, {} as Record<string, TrainingTemplate[]>);

      setDayAssignments(mappedDayAssignments);
    }
  }, [plan, trainingTemplates, props.type]);

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return trainingTemplates.filter((template) => {
      const searchMatch = !query || (template.title || '').toLowerCase().includes(query);
      const sportMatch = !selectedSport || template.sport === selectedSport;
      const sportTypeMatch = !selectedSportType || template.sport_type === selectedSportType;
      const focusMatch = !selectedFocus || template.focus === selectedFocus;
      return searchMatch && sportMatch && sportTypeMatch && focusMatch;
    });
  }, [trainingTemplates, searchQuery, selectedSport, selectedSportType, selectedFocus]);

  const availableTemplates = useMemo(() => {
    if (!selectedDay) return filteredTemplates;
    const assignedTemplateIds = new Set((dayAssignments[selectedDay] || []).map((t) => t.id));
    return filteredTemplates.filter((template) => !assignedTemplateIds.has(template.id));
  }, [filteredTemplates, dayAssignments, selectedDay]);

  const filterOptions = useMemo(() => {
    let available = trainingTemplates;
    if (selectedSport) {
      available = available.filter((t) => t.sport === selectedSport);
    }
    const sports = Array.from(new Set(trainingTemplates.map((t) => t.sport))).filter(Boolean);
    const sportTypes = Array.from(new Set(available.map((t) => t.sport_type))).filter(Boolean);
    if (selectedSportType) {
      available = available.filter((t) => t.sport_type === selectedSportType);
    }
    const focuses = Array.from(new Set(available.map((t) => t.focus))).filter(Boolean);
    return { sports, sportTypes, focuses };
  }, [trainingTemplates, selectedSport, selectedSportType, selectedFocus]);

  useEffect(() => {
    if (selectedSport) {
      const availableSportTypes = Array.from(
        new Set(trainingTemplates.filter((t) => t.sport === selectedSport).map((t) => t.sport_type))
      ).filter(Boolean);
      if (selectedSportType && !availableSportTypes.includes(selectedSportType)) {
        setSelectedSportType('');
      }
    }
  }, [trainingTemplates, selectedSport, selectedSportType]);

  useEffect(() => {
    let available = trainingTemplates;
    if (selectedSport) {
      available = available.filter((t) => t.sport === selectedSport);
    }
    if (selectedSportType) {
      available = available.filter((t) => t.sport_type === selectedSportType);
    }
    const availableFocuses = Array.from(new Set(available.map((t) => t.focus))).filter(Boolean);
    if (selectedFocus && !availableFocuses.includes(selectedFocus)) {
      setSelectedFocus('');
    }
  }, [trainingTemplates, selectedSport, selectedSportType, selectedFocus]);

  const handleDayClick = (day: string) => {
    setSelectedDay(selectedDay === day ? null : day);
    setShowDayWarning(false);
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors<PlanField> = {};
    if (title.trim().length < 1) errors.title = 'Title is required';
    if (description.trim().length < 1) errors.description = 'Description is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePlan = () => {
    resetFieldErrors();
    if (validateForm()) {
      setLoading(true);
      const payload = buildPlanPayload();
      trainingLibraryService
        .createWeeklyTrainingPlan(props.team.id, payload)
        .then(() => {
          setLoading(false);
          showSnackbar('Weekly plan created successfully', 'success');
          props.onClose();
        })
        .catch(() => {
          setLoading(false);
          showSnackbar('There was an error while creating weekly plan. Please try again later', 'error');
        });
    }
  };

  const handleUpdatePlan = () => {
    resetFieldErrors();
    if (validateForm()) {
      setLoading(true);
      const payload = buildPlanPayload();
      trainingLibraryService
        .updateWeeklyTrainingPlan(props.team.id, props.planId, payload)
        .then(() => {
          setLoading(false);
          showSnackbar('Weekly plan updated successfully', 'success');
          props.onClose();
        })
        .catch(() => {
          setLoading(false);
          showSnackbar('There was an error while updating weekly plan. Please try again later', 'error');
        });
    }
  };

  const buildPlanPayload = () => {
    const planPayload = Object.fromEntries(
      DAYS_OF_WEEK.map((day) => [day.toLowerCase(), (dayAssignments[day] || []).map((t) => t.id)])
    );
    return {
      title,
      description,
      plan: planPayload,
    };
  };

  const handleAddTemplate = (template: TrainingTemplate) => {
    if (!selectedDay) {
      setShowDayWarning(true);
      return;
    }

    const dayTemplates = dayAssignments[selectedDay] || [];
    if (dayTemplates.some((t) => t.id === template.id)) return;

    setDayAssignments({
      ...dayAssignments,
      [selectedDay]: [...dayTemplates, template],
    });
  };

  const handleRemoveTemplate = (day: string, templateId: string) => {
    const dayTemplates = dayAssignments[day] || [];
    setDayAssignments({
      ...dayAssignments,
      [day]: dayTemplates.filter((t) => t.id !== templateId),
    });
  };

  const getSortedDayTemplates = (day: string): TrainingTemplate[] => {
    const templates = dayAssignments[day] || [];
    return [...templates].sort((a, b) => {
      try {
        let timeA: number;
        let timeB: number;

        if (a.time.includes('T') || a.time.includes('-')) {
          timeA = parseISO(a.time).getTime();
        } else {
          timeA = new Date(`2000-01-01T${a.time}`).getTime();
        }

        if (b.time.includes('T') || b.time.includes('-')) {
          timeB = parseISO(b.time).getTime();
        } else {
          timeB = new Date(`2000-01-01T${b.time}`).getTime();
        }

        return timeA - timeB;
      } catch {
        return a.time.localeCompare(b.time);
      }
    });
  };

  const handleTemplateClick = (templateId: string) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId);
  };

  const getTrainingTemplates = () => {
    setLoading(true);
    trainingLibraryService
      .getTrainingLibrary(props.team.id)
      .then((response) => {
        setTrainingTemplates(response.data.data);
        setLoading(false);
        setTemplateError(null);
      })
      .catch((fetchError: any) => {
        setLoading(false);
        if (fetchError.response?.status === 404) {
          setTrainingTemplates([]);
          setTemplateError(null);
        } else {
          setTemplateError(
            fetchError.response?.data?.message ||
              'There was an error while fetching training templates. Please try again later'
          );
          showSnackbar(
            fetchError.response?.data?.message ||
              'There was an error while fetching training templates. Please try again later',
            'error'
          );
        }
      });
  };

  const getWeeklyPlan = () => {
    if (!props.planId) return;

    setLoading(true);
    trainingLibraryService
      .getWeeklyTrainingPlan(props.team.id, props.planId)
      .then((response) => {
        setPlan(response.data.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        showSnackbar('There was an error while fetching weekly plan. Please try again later', 'error');
      });
  };

  const filterSelectSx = (selected: boolean) => ({
    borderRadius: '10px',
    height: '38px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: selected ? palette.primary.main : palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
    },
    '& .MuiSelect-select': {
      color: selected ? palette.primary.main : palette.text.primary,
      paddingRight: '28px !important',
    },
    '& .MuiSelect-icon': {
      color: selected ? palette.primary.main : palette.text.secondary,
    },
  });

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      closeAfterTransition
      disableEscapeKeyDown={false}
      className="coe-weekly-plan-builder-container"
    >
      <Fade in={props.open} timeout={500}>
        <Paper className="coe-weekly-plan-builder-paper">
          {loading ? (
            <Box className="coe-weekly-plan-builder-loading">
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <Box className="coe-weekly-plan-builder-gradient-header">
                <IconButton
                  onClick={props.onClose}
                  className="coe-weekly-plan-builder-gradient-close"
                  aria-label="Close"
                  size="small"
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </IconButton>
                <Box className="coe-weekly-plan-builder-gradient-title-block">
                  <Box className="coe-weekly-plan-builder-gradient-icon">
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <h3 className="coe-weekly-plan-builder-gradient-title">Weekly Plan Builder</h3>
                    <p className="coe-weekly-plan-builder-gradient-subtitle">
                      Design your weekly training schedule from saved sessions
                    </p>
                  </Box>
                </Box>
              </Box>

              <Box className="coe-weekly-plan-builder-content">
                <div className="wpb-form-row">
                  <TextField
                    id="wpb-field-title"
                    required
                    placeholder="Plan Title *"
                    name="title"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      clearFieldError('title');
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!fieldErrors.title}
                    helperText={fieldErrors.title || ''}
                    className="wpb-input"
                  />
                  <TextField
                    id="wpb-field-description"
                    required
                    placeholder="Description *"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      clearFieldError('description');
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!fieldErrors.description}
                    helperText={fieldErrors.description || ''}
                    className="wpb-input wpb-input-desc"
                  />
                </div>

                {showDayWarning ? (
                  <Box className="wpb-day-warning">
                    <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: palette.warning.main, flexShrink: 0 }} />
                    <Typography className="wpb-day-warning-text">
                      Select a day first — click a day on the left, then choose a session from the library.
                    </Typography>
                    <IconButton size="small" onClick={() => setShowDayWarning(false)} aria-label="Dismiss warning">
                      <CloseIcon sx={{ fontSize: 12, color: palette.warning.main }} />
                    </IconButton>
                  </Box>
                ) : null}

                <div className="wpb-main-columns">
                  <Typography className="wpb-schedule-label">
                    Schedule <span className="wpb-schedule-hint">— click a day, then a session to assign</span>
                  </Typography>

                  <div className="wpb-day-list">
                    {DAYS_OF_WEEK.map((day, index) => {
                      const isSelected = selectedDay === day;
                      const dayTemplates = getSortedDayTemplates(day);
                      return (
                        <div
                          key={day}
                          className={`wpb-day-row${isSelected ? ' wpb-day-row-selected' : ''}`}
                          onClick={() => handleDayClick(day)}
                        >
                          <div className="wpb-day-row-label">
                            <span className="wpb-day-abbr">{DAY_ABBR[index]}</span>
                            <span className="wpb-day-name">{day}</span>
                          </div>
                          <div className="wpb-day-sessions">
                            {dayTemplates.length === 0 ? (
                              <Typography className="wpb-day-empty">No sessions</Typography>
                            ) : (
                              dayTemplates.map((template) => {
                                const sessionTime = formatSessionTime(template.time);
                                return (
                                  <div
                                    key={`${day}-${template.id}`}
                                    className="wpb-day-session"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveTemplate(day, template.id);
                                    }}
                                    title={`Remove ${template.title}${sessionTime ? ` (${sessionTime})` : ''}`}
                                  >
                                    {sessionTime ? (
                                      <Typography className="wpb-day-session-time">{sessionTime}</Typography>
                                    ) : null}
                                    <Typography className="wpb-day-session-title">{template.title}</Typography>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="wpb-library-column">
                    <div className="wpb-library-panel">
                      <div className="wpb-library-header">
                        <Typography className="wpb-library-title">Training Library</Typography>
                        <Typography className="wpb-library-subtitle">
                          Click a session to assign it to the selected day
                        </Typography>
                      </div>
                      <div className="wpb-library-body">
                        <div className="wpb-library-filters">
                          <TextField
                            className="wpb-library-search"
                            placeholder="Search trainings..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 14, color: palette.text.disabled }} />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 90 }}>
                            <Select
                              value={selectedSport}
                              onChange={(e) => setSelectedSport(e.target.value)}
                              displayEmpty
                              IconComponent={KeyboardArrowDownIcon}
                              sx={filterSelectSx(Boolean(selectedSport))}
                            >
                              <MenuItem value="">Sport</MenuItem>
                              {filterOptions.sports.map((sport) => (
                                <MenuItem key={sport} value={sport} style={{ fontSize: '13px' }}>
                                  {sport}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 90 }}>
                            <Select
                              value={selectedSportType}
                              onChange={(e) => setSelectedSportType(e.target.value)}
                              displayEmpty
                              IconComponent={KeyboardArrowDownIcon}
                              sx={filterSelectSx(Boolean(selectedSportType))}
                            >
                              <MenuItem value="">Type</MenuItem>
                              {filterOptions.sportTypes.map((sport_type) => (
                                <MenuItem key={sport_type} value={sport_type} style={{ fontSize: '13px' }}>
                                  {sport_type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" variant="outlined" sx={{ minWidth: 90 }}>
                            <Select
                              value={selectedFocus}
                              onChange={(e) => setSelectedFocus(e.target.value)}
                              displayEmpty
                              IconComponent={KeyboardArrowDownIcon}
                              sx={filterSelectSx(Boolean(selectedFocus))}
                            >
                              <MenuItem value="">Focus</MenuItem>
                              {filterOptions.focuses.map((focus) => (
                                <MenuItem key={focus} value={focus} style={{ fontSize: '13px' }}>
                                  {focus}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>

                        <Typography className="wpb-library-section-label">Most Used Trainings</Typography>

                        {templateError ? (
                          <Typography
                            variant="body2"
                            style={{ color: palette.error.dark, textAlign: 'center', padding: 16 }}
                          >
                            {templateError}
                          </Typography>
                        ) : trainingTemplates.length === 0 ? (
                          <Typography
                            variant="body2"
                            style={{ color: palette.text.secondary, textAlign: 'center', padding: 16 }}
                          >
                            No training templates available
                          </Typography>
                        ) : availableTemplates.length === 0 ? (
                          <Typography
                            variant="body2"
                            style={{ color: palette.text.secondary, textAlign: 'center', padding: 16 }}
                          >
                            No templates match the selected filters
                          </Typography>
                        ) : (
                          <Grid container spacing={1.5} className="wpb-templates-grid">
                            {availableTemplates.map((template) => (
                              <Grid item xs={12} sm={6} key={template.id}>
                                <COETrainingTemplateCard
                                  template={template}
                                  expanded={expandedTemplateId === template.id}
                                  onClick={() => handleTemplateClick(template.id)}
                                  actionType="add"
                                  onActionClick={(e) => {
                                    e.stopPropagation();
                                    handleAddTemplate(template);
                                  }}
                                  showExpandedDetails={true}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {props.type === 'create' && (
                  <Button className="wpb-save-btn" variant="contained" onClick={handleCreatePlan}>
                    Create and Save Template
                  </Button>
                )}
                {props.type === 'edit' && (
                  <Button className="wpb-save-btn" variant="contained" onClick={handleUpdatePlan}>
                    Update Plan
                  </Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </Modal>
  );
};

export default COEWeeklyPlanBuilderComponent;
